import { CheckoutReviewAndSubmitContext } from "@husky-hrc/Overrides/Pages/CheckoutReviewAndSubmitPage";
import { CheckoutShippingFormContext } from "@husky-hrc/Overrides/Pages/CheckoutShippingPage";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconProps } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import ChevronUp from "@insite/mobius/Icons/ChevronUp";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    pages = "pages",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.pages]: CheckoutHeaderModel[];
    };
}
interface CheckoutHeaderModel {
    fields: {
        pageType: "CheckoutShippingPage" | "CheckoutReviewAndSubmitPage";
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const pageTitle = getCurrentPage(state).fields.title;
    const pageType = getCurrentPage(state).type;
    const pages = props.fields.pages.map(page => getPageLinkByPageType(state, page.fields.pageType));
    const billingAndShippingPage = getPageLinkByPageType(state, "CheckoutShippingPage");
    const cart = getCurrentCartState(state).value;
    return {
        pageTitle,
        pageType,
        pages,
        billingAndShippingPage,
        cart,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory;

export interface CheckoutHeaderStyles {
    containerStyles?: InjectableCss;
    linkStyles?: LinkPresentationProps;
    pageTitleStyles?: InjectableCss;
    iconStyles?: IconProps;
    shoppingCartMenu?: InjectableCss;
    shoppingCartButton?: ClickablePresentationProps;
    shoppingCartText?: TypographyPresentationProps;
    priceText?: TypographyPresentationProps;
}

export const checkoutHeaderStyles: CheckoutHeaderStyles = {
    containerStyles: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-evenly;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-top: 15px;
                        `,
                        null,
                        null,
                        css`
                            margin-top: 0;
                            margin-bottom: 50px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    linkStyles: {
        color: "common.backgroundContrast",
        typographyProps: {
            transform: "uppercase",
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: 12px;
                                line-height: 15px;
                            `,
                            null,
                            null,
                            css`
                                font-size: 14px;
                                line-height: 17px;
                            `,
                        ],
                        "min",
                    )}
            `,
        },
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 20px;
                        `,
                        null,
                        null,
                        css`
                            margin-bottom: 0;
                            width: 50%;
                            align-items: flex-start;
                            justify-content: center;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    pageTitleStyles: {
        css: css`
            outline: none;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 20px;
                            font-weight: 900;
                            letter-spacing: 1px;
                            line-height: 32px;
                            color: ${theme.colors.text.main};
                            margin-bottom: 30px;
                        `,
                        null,
                        null,
                        css`
                            font-size: 26px;
                            margin-bottom: 20px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    iconStyles: {
        size: 14,
        color: "text.main",
        css: css`
            :last-of-type {
                display: none;
            }
        `,
    },
    shoppingCartMenu: {
        css: css`
            display: flex;
            align-items: center;
            padding: 10px 25px;
            margin: 0 -25px;
            background-color: #f6f5fb;
        `,
    },
    shoppingCartButton: {
        css: css`
            flex-grow: 1;
        `,
    },
    shoppingCartText: {
        color: "danger.main",
        size: 16,
        weight: 600,
        lineHeight: "26px",
    },
    priceText: {
        color: "#3a3a3a",
        weight: 900,
        size: 18,
        lineHeight: "28px",
        css: css`
            margin-left: auto;
        `,
    },
};

const PageTab = styled.div<{ selected: boolean }>`
    text-transform: uppercase;
    font-weight: 600;
    color: ${props => (props.selected ? getColor("danger.main") : getColor("common.backgroundContrast"))};
    ${({ theme, selected }: { theme: BaseTheme; selected: boolean }) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                    font-size: 12px;
                    letter-spacing: 0;
                    line-height: 15px;
                    margin-bottom: 20px;
                `,
                null,
                null,
                css`
                    margin-bottom: 0;
                    height: 54px;
                    width: 50%;
                    text-align: center;
                    font-size: 14px;
                    line-height: 17px;
                    border-bottom: ${selected ? "4px solid" : "1px solid"};
                    border-color: ${selected ? theme.colors.danger.main : "#e4eaee"};
                `,
            ],
            "min",
        )}
`;

const styles = checkoutHeaderStyles;

const CheckoutHeader: React.FC<Props> = ({ pageTitle, pageType, pages, billingAndShippingPage, cart, history }) => {
    const { showShoppingCartBillingAndShipping, setShowShoppingCartBillingAndShipping } =
        React.useContext(CheckoutShippingFormContext);
    const { showShoppingCartReviewAndSubmit, setShowShoppingCartReviewAndSubmit } =
        React.useContext(CheckoutReviewAndSubmitContext);
    const onPlaceOrderPage = pageTitle !== billingAndShippingPage?.title;

    const shouldShowShoppingCart = () => {
        return onPlaceOrderPage ? showShoppingCartReviewAndSubmit : showShoppingCartBillingAndShipping;
    };

    const handleShowShoppingCart = () => {
        if (onPlaceOrderPage) {
            setShowShoppingCartReviewAndSubmit();
        } else {
            setShowShoppingCartBillingAndShipping();
        }
    };

    const isClickable = (page: PageLinkModel) => {
        return page.title === billingAndShippingPage?.title && onPlaceOrderPage;
    };

    return (
        <>
            <Hidden below="lg">
                <StyledWrapper role="region" aria-label={translate("Checkout Steps")} {...styles.containerStyles}>
                    {pages &&
                        pages.map(
                            (page, index) =>
                                // eslint-disable-next-line react/jsx-key
                                page &&
                                (isClickable(page) ? (
                                    <Link {...styles.linkStyles} href={page.url}>{`${index + 1} ${page.title}`}</Link>
                                ) : (
                                    <PageTab
                                        selected={pageType === page.type}
                                        aria-current={pageType === page.type && "page"}
                                    >
                                        {`${index + 1} ${page.title}`}
                                    </PageTab>
                                )),
                        )}
                </StyledWrapper>
                <Typography {...styles.pageTitleStyles} variant="h2" as="h1" tabIndex={-1} data-id="PageTitle">
                    {pageTitle}
                </Typography>
            </Hidden>
            <Hidden above="md">
                <StyledWrapper {...styles.shoppingCartMenu}>
                    <Clickable
                        type="button"
                        aria-expanded={shouldShowShoppingCart()}
                        {...styles.shoppingCartButton}
                        onClick={handleShowShoppingCart}
                    >
                        <Typography {...styles.shoppingCartText}>
                            {translate(shouldShowShoppingCart() ? "Hide Shopping Cart" : "Show Shopping Cart")}
                        </Typography>
                        <Icon src={shouldShowShoppingCart() ? ChevronUp : ChevronDown} color="danger.main" size={18} />
                    </Clickable>
                    <Typography {...styles.priceText}>{cart?.orderGrandTotalDisplay}</Typography>
                </StyledWrapper>
                <StyledWrapper role="region" aria-label={translate("Checkout Steps")} {...styles.containerStyles}>
                    {pages &&
                        pages.map(
                            page =>
                                page && (
                                    <>
                                        {isClickable(page) ? (
                                            <Link {...styles.linkStyles} href={page.url}>
                                                {page.title}
                                            </Link>
                                        ) : (
                                            <PageTab
                                                selected={pageType === page.type}
                                                aria-current={pageType === page.type && "page"}
                                            >
                                                {page.title}
                                            </PageTab>
                                        )}
                                        <Icon {...styles.iconStyles} src={ChevronRight} />
                                    </>
                                ),
                        )}
                </StyledWrapper>
                <Typography {...styles.pageTitleStyles} variant="h2" as="h1" tabIndex={-1} data-id="PageTitle">
                    {pageTitle}
                </Typography>
            </Hidden>
        </>
    );
};

// For the time being, this will only be these two fields until we get the number of
// checkout screens resolved with AB&C
const defaultValue: CheckoutHeaderModel[] = [
    {
        fields: {
            pageType: "CheckoutShippingPage",
        },
    },
    {
        fields: {
            pageType: "CheckoutReviewAndSubmitPage",
        },
    },
];

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(CheckoutHeader)),
    definition: {
        group: "Checkout" as any,
        displayName: "Checkout Header",
        fieldDefinitions: [
            {
                name: fields.pages,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.pageType;
                },
                defaultValue,
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "pageType",
                        editorTemplate: "DropDownField",
                        options: [{ value: "CheckoutShippingPage" }, { value: "CheckoutReviewAndSubmitPage" }],
                        displayName: "Select Page Type",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
